import { z } from "zod";

/** Feature-toggle keys = the Free vs Pro matrix in spec Appendix B. Pro always gets all of them. */
export const FEATURE_KEYS = [
  "capture_pdf_slides",
  "capture_docx",
  "capture_text",
  "capture_images",
  "capture_record",
  "capture_audio_import",
  "web_audio_upload",
  "web_bulk_upload",
  "flashcards",
  "practice_basic",
  "practice_exam_formats",
  "study_guide",
  "mind_map_view",
  "mind_map_edit",
  "verify_flag",
  "card_edit",
  "offline_review",
  "search",
  "tutor",
  "ai_hint",
  "tutor_mnemonics",
  "tutor_general_knowledge",
  "explain_simpler",
  "tutor_to_questions",
  "diagram_lab",
  "patient_cases",
  "quiz_tutor_mode",
  "quiz_exam_mode",
  "mock_quick",
  "mock_standard",
  "mock_full",
  "mock_adaptive",
  "streaks",
  "xp_badges",
  "weekly_report",
  "cohort_percentile",
  "exam_plan_basic",
  "ai_pacing_plan",
  "forecast",
  "ai_coach",
  "share_sets",
  "join_shared",
  "export_json",
  "export_csv_anki",
  "notify_set_ready",
  "email_weekly_report",
  "notify_daily_reminder",
  "priority_generation",
  "ai_paid_tier",
] as const;
export const featureKeySchema = z.enum(FEATURE_KEYS);
export type FeatureKey = z.infer<typeof featureKeySchema>;

/** Monthly / per-set allowances. `null` = unlimited (Pro). */
export const LIMIT_KEYS = [
  "sets_per_month",
  "tutor_questions_per_set",
  "voice_minutes_per_month",
  "web_audio_clips_per_month",
  "quick_mocks_per_month",
  "diagrams_per_month",
  "core_questions_per_day",
] as const;
export const limitKeySchema = z.enum(LIMIT_KEYS);
export type LimitKey = z.infer<typeof limitKeySchema>;

const limitValue = z.number().int().nonnegative().nullable();
export const limitsSchema = z.object({
  sets_per_month: limitValue,
  tutor_questions_per_set: limitValue,
  voice_minutes_per_month: limitValue,
  /** v1.6: website audio clips transcribed by AI (published configs from before v1.6 get the default). */
  web_audio_clips_per_month: limitValue.default(3),
  quick_mocks_per_month: limitValue,
  diagrams_per_month: limitValue,
  /**
   * OD-5 (2026-10-21): Free answers this many StudyWard Core library questions a day. Practice from the student's
   * own material stays unlimited — a free feature is never taken away. Configs from before get the default.
   */
  core_questions_per_day: limitValue.default(15),
});
export type Limits = z.infer<typeof limitsSchema>;

export const PLAN_KEYS = ["free", "pro_monthly", "pro_yearly"] as const;
export const planKeySchema = z.enum(PLAN_KEYS);
export type PlanKey = z.infer<typeof planKeySchema>;

const usdPrice = z.number().nonnegative().multipleOf(0.01);

export const planSchema = z.object({
  key: planKeySchema,
  name: z.string().min(1),
  billing_period: z.enum(["none", "month", "year"]),
  display_price_usd: usdPrice,
  trial_days: z.number().int().min(0).max(90),
  /** RevenueCat identifiers; empty until the RevenueCat account exists (§15 #5). */
  revenuecat_product_id: z.string(),
  revenuecat_offering_id: z.string(),
});
export type Plan = z.infer<typeof planSchema>;

const pricingOfferShape = {
  currency: z.literal("USD"),
  plans: z.array(planSchema).length(3),
  free_limits: limitsSchema,
  /** Features included in Free; Pro/trial include every FeatureKey. */
  free_features: z.array(featureKeySchema),
  max_upload_bytes: z.number().int().positive(),
  max_paste_chars: z.number().int().positive(),
  /** Website audio clips (v1.6 §6.2): longest clip per plan, and Pro's daily fair-use total. */
  web_audio: z
    .object({
      free_max_seconds: z.number().int().positive(),
      pro_max_seconds: z.number().int().positive(),
      pro_seconds_per_day: z.number().int().positive(),
    })
    .default({ free_max_seconds: 300, pro_max_seconds: 600, pro_seconds_per_day: 7200 }),
};

function checkOffer(cfg: { plans: Plan[]; free_features: FeatureKey[] }, ctx: z.RefinementCtx) {
  const keys = cfg.plans.map((p) => p.key);
  for (const key of PLAN_KEYS) {
    if (!keys.includes(key)) ctx.addIssue({ code: "custom", message: `Missing plan ${key}`, path: ["plans"] });
  }
  if (new Set(cfg.free_features).size !== cfg.free_features.length) {
    ctx.addIssue({ code: "custom", message: "Duplicate free feature", path: ["free_features"] });
  }
  if (cfg.free_features.includes("ai_paid_tier")) {
    ctx.addIssue({ code: "custom", message: "Free cannot include the paid AI tier", path: ["free_features"] });
  }
}

export const pricingConfigSchema = z
  .object({
    version: z.number().int().positive(),
    ...pricingOfferShape,
    published_at: z.string().nullable(),
    /**
     * Server-computed, never stored (spec v1.7): true only when the paid AI keys are configured, so Pro material
     * really is processed on tiers that don't use it for training. Until then the promise isn't shown anywhere.
     */
    pro_private_ai: z.boolean().default(false),
  })
  .superRefine(checkOffer);
/** An offer as Admin edits it, before publishing assigns the version (§9, Admin #10). */
export const pricingDraftSchema = z.object(pricingOfferShape).superRefine(checkOffer);
export type PricingDraft = z.infer<typeof pricingDraftSchema>;
export type PricingConfig = z.infer<typeof pricingConfigSchema>;

/** Seed defaults (spec §9, Appendix B — owner decisions 2026-10-18). Runtime reads pricing_config, never this. */
export const SEED_PRICING_CONFIG: PricingConfig = {
  version: 1,
  currency: "USD",
  plans: [
    { key: "free", name: "Free", billing_period: "none", display_price_usd: 0, trial_days: 0, revenuecat_product_id: "", revenuecat_offering_id: "" },
    // OD-5 (2026-10-21): $49.99 a year with a 7-day trial; monthly has no trial.
    { key: "pro_monthly", name: "Pro Monthly", billing_period: "month", display_price_usd: 11.99, trial_days: 0, revenuecat_product_id: "", revenuecat_offering_id: "" },
    { key: "pro_yearly", name: "Pro Yearly", billing_period: "year", display_price_usd: 49.99, trial_days: 7, revenuecat_product_id: "", revenuecat_offering_id: "" },
  ],
  free_limits: {
    sets_per_month: 3,
    tutor_questions_per_set: 10,
    voice_minutes_per_month: 60,
    web_audio_clips_per_month: 3,
    quick_mocks_per_month: 1,
    diagrams_per_month: 1,
    core_questions_per_day: 15,
  },
  free_features: [
    "capture_pdf_slides",
    "capture_docx",
    "capture_text",
    "capture_images",
    "capture_record",
    "web_audio_upload",
    "web_bulk_upload",
    "flashcards",
    "practice_basic",
    "practice_exam_formats",
    "study_guide",
    "mind_map_view",
    "verify_flag",
    "search",
    "tutor",
    "ai_hint",
    "diagram_lab",
    "quiz_tutor_mode",
    "quiz_exam_mode",
    "mock_quick",
    "streaks",
    "xp_badges",
    "weekly_report",
    "cohort_percentile",
    "exam_plan_basic",
    "share_sets",
    "join_shared",
    "export_json",
    "notify_daily_reminder",
  ],
  max_upload_bytes: 50 * 1024 * 1024,
  max_paste_chars: 100_000,
  web_audio: { free_max_seconds: 300, pro_max_seconds: 600, pro_seconds_per_day: 7200 },
  published_at: null,
  pro_private_ai: false,
};

/** Effective plan for entitlement checks: trial counts as Pro (spec §16 Step 8). */
export type EffectivePlan = "free" | "pro";

export function hasFeature(config: PricingConfig, plan: EffectivePlan, feature: FeatureKey): boolean {
  return plan === "pro" || config.free_features.includes(feature);
}

/** Allowance for a limit; `null` = unlimited. Pro is unlimited (fair-use caps live in job config, §6.7). */
export function limitFor(config: PricingConfig, plan: EffectivePlan, key: LimitKey): number | null {
  return plan === "pro" ? null : config.free_limits[key];
}

/** Yearly saving vs 12 × monthly, as a whole percent — for "save N%" copy rendered from config. */
/** Plan highlights for pricing cards (site, web billing). Show one only while the live config keeps it in that plan. */
export const FREE_HIGHLIGHT_FEATURES = ["flashcards", "practice_exam_formats", "tutor", "web_bulk_upload", "streaks"] as const satisfies readonly FeatureKey[];
export const PRO_HIGHLIGHT_FEATURES = [
  "mock_full",
  "forecast",
  "patient_cases",
  "tutor_general_knowledge",
  "ai_pacing_plan",
  "offline_review",
  "share_sets",
  "export_csv_anki",
] as const satisfies readonly FeatureKey[];

export function yearlySavingPercent(config: PricingConfig): number {
  const monthly = config.plans.find((p) => p.key === "pro_monthly")?.display_price_usd ?? 0;
  const yearly = config.plans.find((p) => p.key === "pro_yearly")?.display_price_usd ?? 0;
  if (monthly <= 0) return 0;
  return Math.max(0, Math.round((1 - yearly / (monthly * 12)) * 100));
}

/** Data-use notes (spec §11.3, v1.7). Which one applies depends on whether Pro really runs on paid AI tiers yet. */
export const AI_DATA_NOTES = {
  freePlan: "On the Free plan, our AI providers may use uploaded material to improve their services. Pro keeps it private.",
  everyone: "Our AI providers may use uploaded material to improve their services. Please don't upload real patient details.",
  proPromise: "On Pro, your material is processed only on our AI providers' paid tiers and is never used to train AI models. Upgrading applies to material you add from now on.",
} as const;

/**
 * The data-use note to show on upload and signup screens: the Free note while Pro is genuinely private, otherwise
 * the note for everyone (Pro included). Null for Pro students when nothing needs disclosing.
 */
export function aiDataNote(config: Pick<PricingConfig, "pro_private_ai">, plan: EffectivePlan | null): string | null {
  if (!config.pro_private_ai) return AI_DATA_NOTES.everyone;
  return plan === "pro" ? null : AI_DATA_NOTES.freePlan;
}

/** Pro-only features for paywalls, without the privacy promise until it's true (v1.7). */
export function proOnlyFeatures(config: Pick<PricingConfig, "free_features" | "pro_private_ai">, keys: readonly FeatureKey[] = FEATURE_KEYS): FeatureKey[] {
  return keys.filter((k) => !config.free_features.includes(k) && (k !== "ai_paid_tier" || config.pro_private_ai));
}

/** Plain-English feature names for paywalls and plan pages, keyed like the Appendix B matrix. */
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  capture_pdf_slides: "PDF and slides",
  capture_docx: "Word documents",
  capture_text: "Pasted text",
  capture_images: "Photos and screenshots",
  capture_record: "Record lectures with an on-phone transcript",
  capture_audio_import: "Import audio files on your phone",
  web_audio_upload: "Upload audio clips on the website",
  web_bulk_upload: "Bulk upload on the web",
  flashcards: "Flashcards with spaced repetition",
  practice_basic: "Practice questions (multiple choice, select-all)",
  practice_exam_formats: "Next Generation NCLEX question formats",
  study_guide: "Study guide",
  mind_map_view: "Mind map",
  mind_map_edit: "Edit your mind maps",
  verify_flag: "Check sources and flag errors",
  card_edit: "Edit and create cards",
  offline_review: "Review offline",
  search: "Search",
  tutor: "Ask Hoot",
  ai_hint: "AI hints in practice",
  tutor_mnemonics: "Hoot mnemonics",
  tutor_general_knowledge: "Hoot general medical knowledge",
  explain_simpler: "Explain simpler",
  tutor_to_questions: "Turn a chat into questions",
  diagram_lab: "Diagram Lab",
  patient_cases: "Patient cases",
  quiz_tutor_mode: "Quiz, tutor mode",
  quiz_exam_mode: "Quiz, timed exam mode",
  mock_quick: "Quick mock exams",
  mock_standard: "Standard mock exams",
  mock_full: "Full-length exam simulations",
  mock_adaptive: "Adaptive (CAT) mode",
  streaks: "Streaks and activity calendar",
  xp_badges: "XP, levels and badges",
  weekly_report: "Weekly report",
  cohort_percentile: "Cohort percentile",
  exam_plan_basic: "Exam countdown plan",
  ai_pacing_plan: "AI pacing plan, re-planned daily",
  forecast: "Exam forecast and mastery map",
  ai_coach: "AI coach fix-list",
  share_sets: "Share sets with classmates",
  join_shared: "Join shared sets",
  export_json: "Export your data (JSON)",
  export_csv_anki: "Export to CSV and Anki",
  notify_set_ready: "Set-ready notifications",
  email_weekly_report: "Weekly report by email",
  notify_daily_reminder: "Daily review reminder",
  priority_generation: "Priority generation",
  ai_paid_tier: "Your material is never used to train AI",
};
